import React, { useState } from "react";
import {
  Trash2,
  AlertTriangle,
  Calendar,
  Filter,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";

import { getCleanupPreview, executeCleanup } from "@/api/notifications/cleanup";
import type { CleanupFilters, CleanupPreview } from "@/types/notifications";

export default function NotificationCleanup() {
  const { toast } = useToast();
  const [filters, setFilters] = useState<CleanupFilters>({
    before_date: "",
    template_id: undefined,
    campaign_id: undefined,
    status_filter: undefined,
  });
  const [preview, setPreview] = useState<CleanupPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupPreview | null>(null);

  const handlePreview = async () => {
    if (!filters.before_date && !filters.template_id && !filters.campaign_id && !filters.status_filter) {
      toast({
        title: "Error",
        description: "Please select at least one filter criteria.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      setPreview(null);
      setCleanupResult(null);
      const result = await getCleanupPreview(filters);
      setPreview(result);
    } catch (error) {
      console.error("Error getting cleanup preview:", error);
      toast({
        title: "Error",
        description: "Failed to get cleanup preview. The cleanup RPC may not exist yet.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteCleanup = async () => {
    try {
      setExecuting(true);
      const result = await executeCleanup(filters);
      setCleanupResult(result);
      setPreview(null);
      setConfirmOpen(false);

      toast({
        title: "Cleanup Complete",
        description: `Deleted ${result.delivery_count} deliveries, ${result.mapping_count} mappings, ${result.attempt_count} attempts.`,
      });
    } catch (error) {
      console.error("Error executing cleanup:", error);
      toast({
        title: "Error",
        description: "Failed to execute cleanup. The cleanup RPC may not exist yet.",
        variant: "destructive",
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleFullPurge = () => {
    setFilters({
      before_date: new Date().toISOString().slice(0, 16),
      template_id: undefined,
      campaign_id: undefined,
      status_filter: undefined,
    });
  };

  const totalItems =
    (preview?.delivery_count || 0) +
    (preview?.mapping_count || 0) +
    (preview?.attempt_count || 0);

  return (
    <AdminLayout title="Notification Cleanup">
      <div className="space-y-6 max-w-4xl">
        <div>
          <h2 className="text-2xl font-bold">Notification History Cleanup</h2>
          <p className="text-muted-foreground">
            Remove old notification data to keep your system clean and performant.
          </p>
        </div>

        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            Cleanup operations are irreversible. Use the preview mode to verify
            what will be deleted before executing.
          </AlertDescription>
        </Alert>

        {/* Filter Criteria */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Cleanup Filters
            </CardTitle>
            <CardDescription>
              Select criteria to determine which notification data to remove.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="before_date">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Delete Before Date
                </Label>
                <Input
                  id="before_date"
                  type="datetime-local"
                  value={filters.before_date || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, before_date: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  All notifications created before this date will be affected.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status_filter">Status Filter</Label>
                <Select
                  value={filters.status_filter || "all"}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      status_filter: value === "all" ? undefined : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="failed">Failed Only</SelectItem>
                    <SelectItem value="pending">Pending Only</SelectItem>
                    <SelectItem value="sent">Sent Only</SelectItem>
                    <SelectItem value="delivered">Delivered Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template_id">Template ID (Optional)</Label>
                <Input
                  id="template_id"
                  type="number"
                  value={filters.template_id || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      template_id: e.target.value ? parseInt(e.target.value) : undefined,
                    }))
                  }
                  placeholder="Filter by template ID"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign_id">Campaign ID (Optional)</Label>
                <Input
                  id="campaign_id"
                  type="number"
                  value={filters.campaign_id || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      campaign_id: e.target.value ? parseInt(e.target.value) : undefined,
                    }))
                  }
                  placeholder="Filter by campaign ID"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handlePreview} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Previewing...
                  </>
                ) : (
                  "Preview Cleanup"
                )}
              </Button>
              <Button variant="outline" onClick={handleFullPurge}>
                Full Purge Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results */}
        {preview && (
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader>
              <CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Cleanup Preview
              </CardTitle>
              <CardDescription>
                The following items match your filter criteria and will be
                permanently deleted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-md">
                  <div className="text-2xl font-bold text-red-600">
                    {preview.delivery_count}
                  </div>
                  <div className="text-sm text-muted-foreground">Deliveries</div>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-md">
                  <div className="text-2xl font-bold text-red-600">
                    {preview.mapping_count}
                  </div>
                  <div className="text-sm text-muted-foreground">Mappings</div>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-950/20 rounded-md">
                  <div className="text-2xl font-bold text-red-600">
                    {preview.attempt_count}
                  </div>
                  <div className="text-sm text-muted-foreground">Attempts</div>
                </div>
              </div>

              {totalItems > 0 ? (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setConfirmOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {totalItems} Items
                </Button>
              ) : (
                <p className="text-center text-muted-foreground">
                  No items match the selected criteria.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cleanup Result */}
        {cleanupResult && (
          <Card className="border-green-200 dark:border-green-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <div className="font-medium">Cleanup Complete</div>
                  <div className="text-sm text-muted-foreground">
                    Deleted {cleanupResult.delivery_count} deliveries,{" "}
                    {cleanupResult.mapping_count} mappings,{" "}
                    {cleanupResult.attempt_count} attempts.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirm Cleanup
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. The following data will be
                permanently deleted:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <div className="flex justify-between text-sm">
                <span>Deliveries:</span>
                <span className="font-medium">{preview?.delivery_count}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Mappings:</span>
                <span className="font-medium">{preview?.mapping_count}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Attempts:</span>
                <span className="font-medium">{preview?.attempt_count}</span>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={executing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleExecuteCleanup}
                disabled={executing}
              >
                {executing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Confirm Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
